import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { ExternalLink } from 'lucide-react'
import { useI18n } from '@/i18n'
import { Container } from '@/components/layout/Container'
import { Button } from '@/components/ui/Button'
import { Skeleton } from '@/components/ui/Feedback'
import { useToast } from '@/components/ui/Toast'
import { RequireOwner } from '@/components/auth/RequireOwner'
import { StudioNav } from '@/components/studio/StudioNav'
import { ListingForm } from '@/components/studio/ListingForm'
import { useOwner } from '@/hooks/useOwner'
import { useWatches } from '@/hooks/useWatches'
import type { Watch } from '@/types'

/**
 * Post a listing — and edit one, at `/studio/new?id=<watch>`.
 *
 * One page for both on purpose: an edit form that differs from the create form
 * is how fields quietly go missing from one of them.
 */
export default function StudioEditor() {
  return (
    <RequireOwner>
      <Editor />
    </RequireOwner>
  )
}

function Editor() {
  const { t } = useI18n()
  const { toast } = useToast()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const { user } = useOwner()
  const { watches, collections, loading, create, update } = useWatches()
  const [saving, setSaving] = useState(false)

  const editingId = params.get('id')
  const existing = editingId ? (watches.find((w) => w.id === editingId) ?? null) : null
  const isEditing = !!editingId

  const save = async (data: Partial<Watch>) => {
    setSaving(true)
    if (isEditing && existing) {
      await update(existing.id, data)
      toast(t('studio.updated'))
    } else {
      await create({ ...data, owner_email: user?.email ?? '', views: 0 })
      toast(t('studio.created'))
    }
    setSaving(false)
    navigate('/studio/listings')
  }

  return (
    <Container size="lg" data-upbo-id="page-studio-editor" data-upbo-label="Listing editor">
      <div className="pt-10 pb-6 flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-2">
          <span className="eyebrow">{t('studio.eyebrow')}</span>
          <h1 className="font-display text-[36px] sm:text-[44px] leading-tight">
            {isEditing ? t('studio.formEditTitle') : t('studio.formNewTitle')}
          </h1>
          <p className="text-sm text-text-muted max-w-xl">{t('studio.formSubtitle')}</p>
        </div>

        {isEditing && existing && (
          <Link to={`/collection/${existing.id}`}>
            <Button variant="outline" size="sm" iconRight={<ExternalLink size={14} strokeWidth={1.5} />}>
              {t('studio.viewPublic')}
            </Button>
          </Link>
        )}
      </div>

      <StudioNav />

      <div className="pb-24">
        {loading && isEditing ? (
          <div className="flex flex-col gap-4 pt-8">
            <Skeleton className="h-11 w-full" />
            <Skeleton className="h-11 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : (
          <ListingForm
            // Remounting on the id guarantees the form state matches the row
            // being edited — a stale form silently overwriting the wrong watch
            // is the worst bug this page could have.
            key={editingId ?? 'new'}
            watch={existing}
            collections={collections}
            saving={saving}
            onSubmit={save}
            onCancel={() => navigate('/studio/listings')}
          />
        )}
      </div>
    </Container>
  )
}
